'use client';

import type { RefObject } from 'react';
import type { GmMobileBarTab } from '../../../model/gmDrawerQuery';
import type { MissionTargetActions } from '@/components/features/gamification/missions/components/MissionsGroup/utils/helpers';
import type { GamificationMissionGroups } from '@/components/features/gamification/missions/types';
import { cva } from 'class-variance-authority';
import { useEffect, useMemo, useRef } from 'react';
import { GamificationMissionsContent } from '@/components/features/gamification/missions/components';
import { GamificationRewardsContent } from '@/components/features/gamification/rewards/components';
import { GmBundleOffers } from '@/components/features/gamification/shop/GmBundleOffers';
import { GamificationStreak } from '@/components/features/gamification/streak/components/GamificationStreak';
import { cn } from '@/utils/cn';
import { useGamificationTabs } from '../../../hooks/useGamificationTabs';
import { GamificationTabsList } from '../../GamificationTabsList';

const tabs = [
  { key: 'missions', value: 'missions' },
  { key: 'rewards', value: 'rewards' },
] as const;

const tabPanelVariants = cva('transition-all duration-300 ease-out', {
  variants: {
    active: {
      true: 'relative translate-y-0 opacity-100',
      false: 'pointer-events-none absolute inset-x-0 top-0 max-h-0 translate-y-2 overflow-hidden opacity-0',
    },
  },
});

interface GmMobileBarContentProps {
  activeTab: GmMobileBarTab;
  loading: boolean;
  totalRewards?: number;
  missionGroups: GamificationMissionGroups;
  onActiveTabChange: (tab: GmMobileBarTab) => void;
  onExpired: VoidFunction;
  /** The bar's scroll area, scrolled to the top when a mission opens the Rewards tab. */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export const GmMobileBarContent = ({
  activeTab,
  loading,
  missionGroups,
  totalRewards,
  onActiveTabChange,
  onExpired,
  scrollContainerRef,
}: GmMobileBarContentProps) => {
  const localizedTabs = useGamificationTabs(tabs, totalRewards);
  const shouldScrollToTopRef = useRef(false);

  // Missions leading to daily rewards open the bar's Rewards tab (daily rewards are on top) instead of navigating.
  const missionTargetActions = useMemo<MissionTargetActions>(() => ({
    'daily-rewards': () => {
      shouldScrollToTopRef.current = true;
      onActiveTabChange('rewards');
    },
  }), [onActiveTabChange]);

  // Scroll once the Rewards panel is active, so the scroll area already has the Rewards content.
  useEffect(() => {
    if (activeTab !== 'rewards' || !shouldScrollToTopRef.current) {
      return;
    }

    shouldScrollToTopRef.current = false;
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab, scrollContainerRef]);

  return (
    <>
      <GamificationTabsList
        tabs={localizedTabs}
        activeValue={activeTab}
        className="mb-3"
        variant="mobile"
        titleClassName="truncate text-sm"
        renderTab={({ tab, isActive, ...rest }) => (
          <button
            {...rest}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => onActiveTabChange(tab.value)}
          />
        )}
      />

      <div className="relative">
        {/* Gamification Missions Tab */}
        <div
          aria-hidden={activeTab !== 'missions'}
          className={cn(
            tabPanelVariants({ active: activeTab === 'missions' }),
            'flex flex-col gap-4',
          )}
        >
          <GamificationStreak />
          <div className="flex flex-col gap-5 md:gap-6 md:-my-6">
            <GamificationMissionsContent
              loading={loading}
              onExpired={onExpired}
              missionGroups={missionGroups}
              targetActions={missionTargetActions}
            />
          </div>

        </div>

        {/* Gamification Rewards Tab */}
        <div
          aria-hidden={activeTab !== 'rewards'}
          className={cn(
            tabPanelVariants({ active: activeTab === 'rewards' }),
            'flex flex-col gap-4',
          )}
        >
          <GmBundleOffers />
          <GamificationRewardsContent
            className="gap-4 md:gap-4"
          />
        </div>
      </div>
    </>

  );
};
